import { useRef, useState } from 'react';
import Accordion from '~/components/molecules/accordion/accordion';
import NumberRangeFilter from '~/components/organisms/filters/numberRangeFilter/NumberRangeFilter';
import { Filter_ControlsState, Filter_State } from '~/core/features/filtering/types/filters.type';
import { useSERPActions } from '~/core/state/actions/SERPActionsContext';
import { UpdateFilterControlsCurried } from '~/core/useSearchState';
import { BooleanFilter } from './BooleanFilter';
import styles from './filter.module.scss';
import { KeyWordFilter } from './KeyWordFilter';
import { SliderRangeFilter } from './sliderRangeFilter/SliderRangeFilter';

type FilterProps = {
    filterState: Filter_State;
    filterControls: Filter_ControlsState;
    updateFilterControls: UpdateFilterControlsCurried;
    querySetIdentifier: string;
    currency: string;
};

const useSliders = true;

const Filter = ({ filterState, filterControls, updateFilterControls, querySetIdentifier }: FilterProps) => {
    const [showAllValues, setShowAllValues] = useState<boolean>(false);

    const { resetFilterControls } = useSERPActions();
    const scrollableRef = useRef<HTMLFieldSetElement>(null);

    const isNumberFilter =
        (filterState.type === 'integer' || filterState.type === 'float') &&
        (filterControls.type === 'integer' || filterControls.type === 'float');
    const isKeyWordFilter = filterState.type === 'keyword' && filterControls.type === 'keyword';
    const isBooleanFilter = filterState.type === 'boolean' && filterControls.type === 'boolean';

    const renderSliderRangeFilter =
        isNumberFilter && filterState.values.min.value <= filterState.values.max.value && useSliders;

    const fieldsetClassName = renderSliderRangeFilter
        ? `${styles.serpFieldset} ${styles.serpFieldsetHiddenOverflow}`
        : styles.serpFieldset;

    const [showMoreButton, setShowMoreButton] = useState(true);

    return (
        <Accordion
            showOpen={!filterState.configuration.showCollapsed}
            label={filterState.name}
            showMoreCount={
                undefined !== filterState.configuration.maxValues && isKeyWordFilter
                    ? filterState.values.keywords.length - filterState.configuration.maxValues
                    : 0
            }
            showChevron={true}
            showMoreButton={showMoreButton}
            setShowAllValues={setShowAllValues}
            showAllValues={showAllValues}
            headClassName={isNumberFilter ? styles.filterHeadRightPosition : ''}
            showMoreButtonAttributes={{
                'data-testid': 'filters-show-more',
            }}
            labelAttributes={{
                'data-testid': 'filters-label',
            }}
        >
            <fieldset className={fieldsetClassName} ref={scrollableRef}>
                {/* do not render integer filter if initial max value is smaller than initial min value */}
                {isNumberFilter && filterState.values.min.value <= filterState.values.max.value ? (
                    useSliders ? (
                        <SliderRangeFilter
                            updateFilterControls={updateFilterControls(querySetIdentifier)(filterState.identifier)}
                            filterState={filterState}
                            filterControls={filterControls}
                        />
                    ) : (
                        <NumberRangeFilter
                            updateFilterControls={updateFilterControls(querySetIdentifier)(filterState.identifier)}
                            filterState={filterState}
                            filterControls={filterControls}
                        />
                    )
                ) : null}
                {isKeyWordFilter ? (
                    <KeyWordFilter
                        key={filterState.identifier}
                        filterControls={filterControls}
                        filterState={filterState}
                        showAllValues={showAllValues}
                        updateFilterControls={updateFilterControls(querySetIdentifier)(filterState.identifier)}
                        resetFilterControls={resetFilterControls(querySetIdentifier)(filterState.identifier)}
                        setShowMoreButton={setShowMoreButton}
                        scrollRef={scrollableRef}
                    />
                ) : null}
                {isBooleanFilter ? (
                    <BooleanFilter
                        filterState={filterState}
                        filterControls={filterControls}
                        updateFilterControls={updateFilterControls(querySetIdentifier)(filterState.identifier)}
                    />
                ) : null}
            </fieldset>
        </Accordion>
    );
};

export default Filter;
